import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { FieldControl, FieldGroup, FormBuilder, Validators } from 'react-reactive-form';
import { AppState } from '../../../../store/store.types';
import { NumberInput, TextInput } from '../../../../components/forms/Input';
import Checkbox from '../../../../components/forms/Checkbox';

import { SelectedOption, StatusType } from '../../../../api/api.types';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import Select from '../../../../components/forms/Select';
import StatusLabel from '../../../../components/forms/StatusLabel';
import { ClassifierActionTypes } from '../../types/classifier.types';
import { addNewClassifier, deleteClassifier, editClassifier, getClassifier } from '../../actions/classifier.actions';
import { getClassifierTypeList } from '../../../core/actions/classifiertypes.actions';
import { WithTranslation, withTranslation } from 'react-i18next';
import { ClassifierModel, ClassifierTypeModel, EditClassifierRequest } from '../../../../api/admin/admin.types';

interface ClassifierLocationProps {
  code: string;
}

interface ClassifierDetailProps extends RouteComponentProps<ClassifierLocationProps>, WithTranslation {
  selectedClassifier: ClassifierModel | null;
  classifierTypes: ClassifierTypeModel[];
  getSelectedClassifier: (code: string) => void;
  editClassifier: (code: string, request: EditClassifierRequest) => void;
  addNewClassifier: (request: ClassifierModel) => void;
  deleteClassifier: (code: string) => void;
  getClassifierTypesList: () => void;
  isNewCode?: boolean;
  postSuccess: boolean | null;
  lastFetch: number | null;
}

interface ClassifierDetailState {
  isModalOpen: boolean;
  isEdit: boolean;
}

class ClassifierDetails extends React.Component<ClassifierDetailProps, ClassifierDetailState> {

  readonly state = {
    isModalOpen: false,
    isEdit: false
  };

  form = FormBuilder.group({
    code: [ '', Validators.required ],
    classifierType: [ '', Validators.required ],
    name: [ '', Validators.required ],
    description: [ '' ],
    stringValue: [ '' ],
    longValue: [ '' ],
    active: [ true ]
  });

  componentDidMount() {
    const { code } = this.props.match.params;

    if (!this.props.classifierTypes.length) {
      this.props.getClassifierTypesList();
    }

    if (code) {
      this.props.getSelectedClassifier(code);
    }
  }

  componentDidUpdate(prevProps: ClassifierDetailProps) {
    if ((this.props.isNewCode) && this.props.postSuccess && this.props.selectedClassifier) {
      this.props.history.push(`/admin/classifiers/${this.props.selectedClassifier.code}/view`);
    }
    if (this.props.selectedClassifier && (!prevProps.selectedClassifier || this.props.selectedClassifier !== prevProps.selectedClassifier)) {
      this.updateFormValue();
    }
  }

  updateFormValue = () => {
    if (!this.props.selectedClassifier) {
      return;
    }
    this.form.patchValue({
      code: this.props.selectedClassifier.code,
      classifierType: this.props.selectedClassifier.classifierType,
      name: this.props.selectedClassifier.name,
      description: this.props.selectedClassifier.description,
      stringValue: this.props.selectedClassifier.stringValue,
      longValue: this.props.selectedClassifier.longValue,
      active: this.props.selectedClassifier.statusType === StatusType.ACTIVE
    });
  };

  findClassifierType = () => {
    if (this.props.selectedClassifier) {
      const typeCode = this.props.selectedClassifier.classifierType;
      const type = this.props.classifierTypes.find((m: ClassifierTypeModel) => (m.code === typeCode));

      if (type) {
        return { value: type.code, label: type.name };
      }
    }

    return { value: '', label: 'Select classifier type' };
  };

  onDeleteConfirm = () => {
    const { code } = this.props.match.params;
    this.props.deleteClassifier(code);
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (this.form.invalid) {
      return;
    }
    if (this.props.selectedClassifier) {
      const request: EditClassifierRequest = {
        ...this.props.selectedClassifier,
        name: form.name.value,
        description: form.description.value,
        stringValue: form.stringValue.value,
        longValue: form.longValue.value,
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE
      };
      const { code } = this.props.match.params;

      this.props.editClassifier(code, request);
      this.setState({ isEdit: false });
    } else {
      const request: ClassifierModel = {
        code: form.code.value,
        classifierType: form.classifierType.value,
        name: form.name.value,
        description: form.description.value,
        stringValue: form.stringValue.value,
        longValue: form.longValue.value,
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE
      };

      this.props.addNewClassifier(request);
    }
  };

  onCancel = () => {
    if (this.props.isNewCode) {
      this.props.history.push('/admin/classifiers/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true });
  };

  patchField = (option: SelectedOption, field: string) => {
    this.form.patchValue({ [ field ]: option.value });
  };

  render() {
    if (!this.props.isNewCode && !this.props.selectedClassifier) {
      return null;
    }

    const showForm = this.state.isEdit || this.props.isNewCode;

    const { t } = this.props;

    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/admin', label: 'Admin' },
              { path: '/admin/classifiers', label: t('m2.classifiers') },
              {
                path: null, label: this.props.isNewCode ? t('components.breadcrumb.new_code') :
                  this.props.selectedClassifier && this.props.selectedClassifier.code || ''
              }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1 className="mb-0">{this.props.isNewCode ? t('page_title.add_new_classifier') :
                t('page_title.classifier_details')}</h1>
              {!!this.props.selectedClassifier && !showForm
                && (
                  <div>
                    <button type="button" className="btn btn-primary edit-button" onClick={() => this.onEdit()}>
                      {t('b.edit_Generic edit button').toUpperCase()}
                    </button>
                  </div>
                )}
            </div>
          </div>
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                <hr/>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.classifier.code')}</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                    {
                      this.props.selectedClassifier && !this.props.isNewCode
                      && (
                        <div className="form-control-static">
                          {this.form.controls.code.value}
                        </div>
                      )
                    }
                    {
                      showForm && this.props.isNewCode
                      && (
                        <FieldControl
                          name="code"
                          render={TextInput}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.classifier.name')}</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                    {
                      this.props.selectedClassifier && !showForm
                      && (
                        <div className="form-control-static">
                          {this.form.controls.name.value}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="name"
                          render={TextInput}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label>{t('l.classifier.description')}</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                    {
                      this.props.selectedClassifier && !showForm
                      && (
                        <div className="form-control-static">
                          {this.form.controls.description.value}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="description"
                          render={TextInput}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label>{t('l.classifier.value')}</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                    {
                      this.props.selectedClassifier && !showForm
                      && (
                        <div className="form-control-static">
                          {this.form.controls.stringValue.value}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="stringValue"
                          render={TextInput}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label>{t('l.classifier.numeric_value')}</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                    {
                      this.props.selectedClassifier && !showForm
                      && (
                        <div className="form-control-static">
                          {this.form.controls.longValue.value}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="longValue"
                          render={NumberInput}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.classifier.classifier_type')}</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                    {
                      this.props.selectedClassifier && !this.props.isNewCode
                      && (
                        <div className="form-control-static">
                          {this.form.controls.classifierType.value}
                        </div>
                      )
                    }
                    {
                      showForm && this.props.isNewCode
                      && (
                        <FieldControl
                          strict={false}
                          name="classifierType"
                          render={(props) => (
                            <Select
                              className="mr-1"
                              placeholder={t('ph.classifier.classifier_type')}
                              options={this.props.classifierTypes.map((d) => ({ label: d.name, value: d.code }))}
                              closeMenuOnSelect={false}
                              disableMinWidth
                              hideSelectedOptions={false}
                              label="classifier type"
                              onChange={(value) => this.patchField(value as SelectedOption, 'classifierType')}
                            />
                          )}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.classifier.status')}</label>
                  </div>
                  <div className="col-6 d-flex align-items-center">
                    {
                      this.props.selectedClassifier && !showForm
                      && (
                        <div className="form-control-static">
                          <StatusLabel status={this.props.selectedClassifier.statusType}></StatusLabel>
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="active"
                          render={(props) => <Checkbox {...props} id="statusField" className="mt-1">{
                            t('components.checkbox.classifiers.active')
                          }</Checkbox>}
                        />
                      )
                    }
                  </div>
                </div>
                {showForm && (
                  <div className="row">
                    <div className="col-12 text-right">
                      <button type="button" className="btn btn-outline-secondary btn-lg mr-2" onClick={this.onCancel}>
                        {t('b.cancel_Generic cancel button translation').toUpperCase()}
                      </button>
                      <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>{
                        t('b.save_Generic save button translation').toUpperCase()
                      }</button>
                    </div>
                  </div>
                )}
              </form>
            )}
          />
        </div>
        <ConfirmationModal
          title="Confirm classifier removal"
          content="Are you sure you want to remove this classifier?"
          isOpen={this.state.isModalOpen}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isModalOpen: false })}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedClassifier: state.admin.classifiers.selectedClassifier,
  classifierTypes: state.core.classifierTypes.classifierTypes,
  postSuccess: state.admin.classifiers.postSuccess,
  lastFetch: state.admin.classifiers.lastFetch
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, ClassifierActionTypes>
) => ({
  getSelectedClassifier: (code: string) => dispatch(getClassifier(code)),
  editClassifier: (code: string, request: EditClassifierRequest) => dispatch(editClassifier(code, request)),
  addNewClassifier: (request: ClassifierModel) => dispatch(addNewClassifier(request)),
  deleteClassifier: (code: string) => dispatch(deleteClassifier(code)),
  getClassifierTypesList: () => dispatch(getClassifierTypeList())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(ClassifierDetails));
